// BeforeLoadEditor — page-level "Before Load / Access" section in the Smart
// Page designer's Page panel.
//
// A page may declare a `beforeLoad` business-logic event that runs SERVER-SIDE
// at page-fetch time, BEFORE the page renders, to gate access. It is a
// COMBINATION of a validation and an item_change: it returns an error to DENY
// access (validation), and on success returns data to SET page variables
// (item_change). It is authored with the EXACT same shared Business-Logic
// fields as transaction validations / item_change — function name, description,
// language, functional specification — so the experience is identical.
//
// The function deploys as `<page_obj>_<method>`; its stub is generated on page
// save, authored in the Source editor, and compiled via "Deploy logic".

import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { ShieldCheck } from 'lucide-react'
import type { PageModel } from '@twasta/page-schema'
import type { BusinessLogicValidation } from '@twasta/page-runtime/txn/types'
import BusinessLogicFields from '@/pages/designers/transaction/BusinessLogicFields'
import { deployPageLogic } from '@/api/designers'

interface Props {
  page: PageModel
  project: string
  onPageChange: (p: PageModel) => void
  // Open the BL function in the code editor (owned by PageDesignerPage:
  // saves the page, generates/locates the file, opens the closeable Code tab).
  onOpenCode?: (method?: string) => void
}

interface DeployResult {
  name?: string
  status?: string
  error_message?: string
}

const EMPTY_BL: BusinessLogicValidation = { method: 'before_load', description: '' }

export default function BeforeLoadEditor({ page, project, onPageChange, onOpenCode }: Props) {
  const enabled = !!page.beforeLoad?.enabled
  const objName = page.obj_name
  const bl: BusinessLogicValidation =
    (page.beforeLoad?.business_logic as BusinessLogicValidation | undefined) || EMPTY_BL
  const [deploying, setDeploying] = useState(false)
  const [results, setResults] = useState<DeployResult[] | null>(null)

  const toggle = useCallback(
    (on: boolean) => {
      if (on) {
        onPageChange({
          ...page,
          beforeLoad: {
            enabled: true,
            mode: 'business_logic',
            business_logic: { ...EMPTY_BL, ...(page.beforeLoad?.business_logic || {}) },
          },
        })
      } else {
        const prev = page.beforeLoad
        onPageChange({ ...page, beforeLoad: prev ? { ...prev, enabled: false } : undefined })
      }
    },
    [page, onPageChange],
  )

  const setBl = useCallback(
    (next: BusinessLogicValidation) => {
      onPageChange({
        ...page,
        beforeLoad: {
          enabled: true,
          mode: 'business_logic',
          business_logic: next,
        },
      })
    },
    [page, onPageChange],
  )

  const deploy = useCallback(async () => {
    if (!objName) return
    setDeploying(true)
    setResults(null)
    try {
      const r = await deployPageLogic(project, objName)
      setResults((r.results as DeployResult[]) || [])
      const bad = (r.results || []).filter(
        (x) => (x as DeployResult).status && (x as DeployResult).status !== 'ok',
      )
      if (bad.length === 0) toast.success('Before-load logic deployed')
      else toast.error('Deploy reported issues — see below')
    } catch (e) {
      const err = e as { message?: string }
      toast.error(`Deploy failed: ${err?.message || e}`)
    } finally {
      setDeploying(false)
    }
  }, [project, objName])

  return (
    <div
      style={{
        marginTop: 8,
        padding: '8px 10px',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontWeight: 600,
          fontSize: '0.8125rem',
          color: 'var(--color-text)',
        }}
      >
        <ShieldCheck size={14} />
        Before Load / Access
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.5,
        }}
      >
        Business logic run before this page renders to decide whether the visitor
        may access it. Like a <strong>validation</strong> it can return an error to
        deny; like an <strong>item_change</strong> it can return values to set page
        variables. It receives the URL parameters (e.g. an OTP / invitation token)
        and may also redirect. Runs server-side on every surface.
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 8,
          fontSize: '0.8125rem',
          color: 'var(--color-text)',
          cursor: 'pointer',
        }}
      >
        <input type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} />
        Enable before-load access event
      </label>

      {enabled && (
        <div style={{ marginTop: 4 }}>
          {/* The EXACT shared sub-form used by transaction validations /
              item_change — function name, description, language, spec — plus
              the same "Open in code editor" affordance. */}
          <BusinessLogicFields
            value={bl}
            onChange={setBl}
            onOpenCode={onOpenCode ? () => onOpenCode(bl.method || 'before_load') : undefined}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              onClick={deploy}
              disabled={deploying}
              className="btn btn-sm"
              style={{
                padding: '4px 12px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                background: 'var(--color-bg)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {deploying ? 'Deploying…' : 'Deploy logic'}
            </button>
          </div>

          {results && results.length > 0 && (
            <ul
              style={{
                marginTop: 8,
                paddingLeft: 16,
                fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)',
              }}
            >
              {results.map((r, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  <strong>{r.name || bl.method || 'before_load'}</strong>:{' '}
                  <span
                    style={{
                      color:
                        r.status === 'ok'
                          ? 'var(--color-success, #16a34a)'
                          : 'var(--color-error, #dc2626)',
                    }}
                  >
                    {r.status || 'unknown'}
                  </span>
                  {r.error_message ? ` — ${r.error_message}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
